import React, { useState } from 'react';
import ProgressBar from '@ramonak/react-progress-bar';
import { ChartDateAxis, chartAxisTickClass } from '../chartAxis';
import { scoreBandColor, scoreBandLabelColor } from '../scoreBandColor';
import { meanRoundedPercent } from '../weekdayFilter';
import { clampPercent } from '../utils/clampPercent';
import { formatChartDate } from '../utils/formatDates';

interface Props {
  label: string;
  points: { date: string; percent: number }[];
}

function ParticipationRow({ label, points }: Props): React.ReactElement {
  const actual = clampPercent(meanRoundedPercent(points.map((point) => point.percent)));
  const averageColor = scoreBandColor(actual);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <span className="text-p2 font-medium text-portal-teal">{label}</span>
        <span
          className="tabular-nums text-[12px] font-semibold text-portal-teal"
          aria-label={`${label} average participation ${actual} percent`}
        >
          Average {actual}%
        </span>
      </div>
      <ProgressBar
        completed={actual}
        maxCompleted={100}
        bgColor={averageColor}
        baseBgColor="#e7edf8"
        height="14px"
        borderRadius="9999px"
        isLabelVisible={false}
        className="w-full"
      />
      {points.length > 0 ? (
        <div className="mt-3 flex gap-1.5">
          <div
            className={`flex h-40 flex-col justify-between ${chartAxisTickClass}`}
            aria-hidden="true"
          >
            <span>100</span>
            <span>0</span>
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="grid h-40 items-stretch overflow-visible rounded-md bg-portal-header-wash/80 px-4"
              role="img"
              aria-label={`${label} daily coverage histogram, ${points.length} days`}
              style={{
                gridTemplateColumns: `repeat(${points.length}, minmax(0, 1fr))`,
                columnGap: 3,
              }}
            >
              {points.map((point) => {
                const value = clampPercent(point.percent);
                const day = formatChartDate(point.date);
                const barColor = scoreBandColor(value);
                const labelColor = scoreBandLabelColor(barColor);
                const bubbleAboveBar = value <= 68;
                return (
                  <span
                    key={`${label}-${point.date}`}
                    className="relative h-full min-w-0"
                    onMouseEnter={() => setHoveredDate(point.date)}
                    onMouseLeave={() => setHoveredDate(null)}
                  >
                    <span
                      aria-label={`${label} ${day} ${value} percent`}
                      className="absolute bottom-0 left-0 right-0 rounded-t-[1px]"
                      style={{
                        height: `${value}%`,
                        minHeight: value === 0 ? 2 : undefined,
                        backgroundColor: barColor,
                        opacity: value === 0 ? 0.18 : 0.9,
                      }}
                    />
                    {hoveredDate === point.date ? (
                      <span
                        className="pointer-events-none absolute left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 whitespace-nowrap rounded-lg border border-portal-teal/25 bg-pure-white px-3 py-2 text-portal-teal shadow-[0_6px_16px_-4px_rgba(0,75,77,0.28)]"
                        style={{
                          bottom: bubbleAboveBar
                            ? `calc(${Math.max(value, 8)}% + 12px)`
                            : 'calc(100% - 52px)',
                        }}
                      >
                        <span className="text-[14px] font-semibold leading-none">{day}</span>
                        <span
                          className="rounded-md px-2 py-1 text-[16px] font-bold leading-none tabular-nums"
                          style={{ backgroundColor: barColor, color: labelColor }}
                        >
                          {value}%
                        </span>
                        <span className="absolute left-1/2 top-full -translate-x-1/2 border-x-[9px] border-b-0 border-t-[9px] border-transparent border-t-portal-teal" />
                      </span>
                    ) : null}
                  </span>
                );
              })}
            </div>
            <div className="px-4">
              <ChartDateAxis dates={points.map((point) => point.date)} />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ParticipationRow;
